
<script>
import {
  required,
  numeric,
  sameAs,
  requiredIf,
  maxValue,
  minLength,
  email
} from "@vuelidate/validators";
import {
  tel,
  zipCode,
  numCarteFidelite,
  birthdate,
  checked
} from "@/utils/form-validators";
import FormFieldset from "@/components/molecules/form-fieldset";
import { useForms } from "@/components/organisms/forms/mixin";
import Button from "@/components/atoms/button";
import Form from "@/components/molecules/form";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import FormRadio from "@/components/molecules/form-radio";
import FormSelect from "@/components/molecules/form-select";
import FormCheckbox from "@/components/molecules/form-checkbox";
import FormSelectOption from "@/components/molecules/form-select/form-select-option";
import LinkTo from "@/components/atoms/link-to";
import Captcha from "@/components/molecules/captcha";
import SuccessPopin from "@/components/organisms/forms/successPopin";
import FormSelectOptGroup from "@/components/molecules/form-select/form-select-optgroup";
import ButtonGroup from "@/components/molecules/button-group";
import { concatenateStrings } from "@/composables/text-transformation";

export default {
  name: "ActivateMalinCardForm",
  components: {
    Form,
    FormCheckbox,
    FormSelect,
    FormGroup,
    FormSelectOption,
    FormInput,
    LinkTo,
    Button,
    FormRadio,
    FormFieldset,
    ButtonGroup,
    Captcha,
    SuccessPopin,
    FormSelectOptGroup
  },
  props: {
    client: {
      type: Object,
      required: false,
      default: () => {
        return {};
      }
    },
    emailClient: {
      type: String,
      required: false,
      default: ""
    },
    magasins: {
      type: Array,
      required: true
    },
    magasinId: {
      type: Number,
      required: false,
      default: null
    },
    ...formValidationProps()
  },
  emits: ["save"],
  setup(props) {
    const { redirectToPreviousPage } = useForms();
    const { captcha, v$, getState, getErrorMessage, checkValidation, states } =
      useFormValidation(props);

    return {
      captcha,
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      states,
      redirectToPreviousPage
    };
  },
  data() {
    return {
      model: {
        num_carte: this.client ? this.client.attributes.num_carte_malin : "",
        nom: this.client ? this.client.attributes.nom : "",
        prenom: this.client ? this.client.attributes.prenom : "",
        civilite: this.client ? this.client.attributes.civilite : "",
        email: this.client ? this.client.attributes.email : this.emailClient,
        password: "",
        password_confirmation: "",
        tel: this.client ? this.client.attributes.tel : "",
        adresse: this.client ? this.client.attributes.adresse : "",
        cp: this.client ? this.client.attributes.cp : "",
        ville: this.client ? this.client.attributes.ville : "",
        pays: this.client ? this.client.attributes.pays : "France",
        complement_adresse: this.client
          ? this.client.attributes.complement_adresse
          : "",
        date_naissance: "",
        nombre_enfants: "",
        date_naissance_1er_enfant: "",
        date_naissance_2eme_enfant: "",
        date_naissance_3eme_enfant: "",
        date_naissance_4eme_enfant: "",
        optin_email_suivi_informations_promotionnelles_commerciales: false,
        accepter_conditions_fidelite: false,
        accepter_conditions_compte_client: false,
        id_magasin: this.magasinId ? this.magasinId : "",
        optin_sms_suivi_commande_drive: false,
        create_client: !this.client
      },
      countries: ["France", "Belgique", "Suisse", "Allemagne", "Luxembourg"],
      captchaToken: null
    };
  },
  validations() {
    return {
      model: {
        num_carte: {
          required,
          numCarteFidelite
        },
        nom: {
          required
        },
        prenom: {
          required
        },
        civilite: {
          required
        },
        email: {
          required,
          email
        },
        password: {
          required: requiredIf(function () {
            return !this.client;
          }),
          minLength: minLength(6)
        },
        password_confirmation: {
          required: requiredIf(function () {
            return !this.client;
          }),
          sameAsPassword: sameAs(this.model.password)
        },
        tel: {
          required,
          tel
        },
        adresse: {
          required
        },
        cp: {
          required,
          zipCode
        },
        ville: {
          required
        },
        nombre_enfants: {
          numeric,
          maxValue: maxValue(10)
        },
        date_naissance: {
          birthdate
        },
        date_naissance_1er_enfant: {
          birthdate
        },
        date_naissance_2eme_enfant: {
          birthdate
        },
        pays: {
          required
        },
        date_naissance_3eme_enfant: {
          birthdate
        },
        date_naissance_4eme_enfant: {
          birthdate
        },
        optin_email_suivi_informations_promotionnelles_commerciales: {
          required
        },
        accepter_conditions_fidelite: {
          checked
        },
        accepter_conditions_compte_client: {
          checked
        },
        id_magasin: {
          required
        }
      }
    };
  },
  computed: {
    otherMagasins() {
      if (!this.magasinId) {
        return this.magasins;
      }
      return this.magasins.filter((magasin) => {
        return parseInt(magasin.id) !== this.magasinId;
      });
    },
    currentMagasin() {
      return this.magasins.find((magasin) => {
        return parseInt(magasin.id) === this.magasinId;
      });
    }
  },
  methods: {
    verifyCaptcha(token) {
      if (token) {
        this.captchaToken = token;
      }
      const model = {
        ...this.model,
        nombre_enfants:
          this.model.nombre_enfants === "" ? 0 : this.model.nombre_enfants,
        "g-captcha-response": this.captchaToken
      };
      this.$emit("save", model);
    },
    save() {
      if (this.checkValidation(this.v$)) {
        this.$refs.captcha.execute();
      }
    }
  }
};
</script>
<template>
  <div class="u-mb-xlg u-pl-md u-pr-md u-bgcolor-white u-pb-md">
    <SuccessPopin
      v-if="state===states.SUCCESS"
      @hidden="redirectToPreviousPage()"
    />
    <br>
    <br>
    <Form
      :error="error"
      class="u-mx-xxlg u-mx-lg@md"
      @submit.prevent="save()"
    >
      <FormFieldset
        center
        size="full"
      >
        <FormGroup
          :state="getState(v$,'id_magasin', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <label
            for="selectMagasin"
            class="u-block u-font-13 u-mb-xs u-color-text-light"
          > Votre magasin de référence*
          </label>
          <FormSelect
            id="selectMagasin"
            v-model="model.id_magasin"
            :state="getState(v$,'id_magasin', 'model')"
            :default-value="'Sélectionnez un magasin'"
            size="full"
          >
            <FormSelectOptGroup
              v-if="currentMagasin"
              label="Vous êtes rattaché(e) au magasin"
            >
              <FormSelectOption :value="currentMagasin.id">
                {{ currentMagasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
            <FormSelectOptGroup label="Autres magasins Cora">
              <FormSelectOption
                v-for="magasin in otherMagasins"
                :key="magasin.id"
                :value="magasin.id"
              >
                {{ magasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
          </FormSelect>
          <template #message>
            <p>
              <template v-if="v$.model.id_magasin.required.$invalid">
                Merci de choisir un magasin
              </template>
              <template v-else>
                {{ getErrorMessage("id_magasin") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          class="c-inscription-form__form-group"
          :state="getState(v$,'num_carte', 'model')"
          size="full"
        >
          <FormInput
            id="num_carte"
            v-model.trim="model.num_carte"
            :state="getState(v$,'num_carte', 'model')"
            size="full"
            @blur="v$.model.num_carte.$touch()"
            @input="v$.model.num_carte.$reset()"
          >
            Votre numéro de carte malin *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.num_carte.required.$invalid">
                Merci de saisir votre numéro de carte
              </template>
              <template v-else-if="v$.model.num_carte.numCarteFidelite.$invalid">
                Le numéro que vous avez saisi est invalide
              </template>
              <template v-else>
                {{ getErrorMessage("num_carte") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <label class="u-block u-font-13 u-mb-xs u-color-text-light">Civilité *</label>
        <FormGroup
          :state="getState(v$,'civilite', 'model')"
          inline
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormRadio
            v-model="model.civilite"
            name="civilite"
            value="Mme"
            class="u-mr-lg"
          >
            Madame
          </FormRadio>
          <FormRadio
            v-model="model.civilite"
            name="civilite"
            value="M."
          >
            Monsieur
          </FormRadio>
          <template #message>
            <p>
              <template v-if="v$.model.civilite.required.$invalid">
                Merci de renseigner votre civilité
              </template>
              <template v-else>
                {{ getErrorMessage("civilite") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'nom', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="nom"
            v-model.trim="model.nom"
            :state="getState(v$,'nom', 'model')"
            size="full"
            @blur="v$.model.nom.$touch()"
            @input="v$.model.nom.$reset()"
          >
            Nom *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.nom.required.$invalid">
                Merci de renseigner votre nom
              </template>
              <template v-else>
                {{ getErrorMessage("nom") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'prenom', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="prenom"
            v-model.trim="model.prenom"
            :state="getState(v$,'prenom', 'model')"
            maxlength="40"
            size="full"
            @blur="v$.model.prenom.$touch()"
            @input="v$.model.prenom.$reset()"
          >
            Prénom *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.prenom.required.$invalid">
                Merci de renseigner votre prénom
              </template>
              <template v-else>
                {{ getErrorMessage("prenom") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'email','model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="email2"
            v-model.trim="model.email"
            :disabled="client ? true : false"
            :state="getState(v$,'email','model')"
            size="full"
            @blur="v$.model.email.$touch()"
            @input="v$.model.email.$reset()"
          >
            Email *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.email.required.$invalid">
                Merci de confirmer votre adresse e-mail
              </template>
              <template v-else-if="v$.model.email.email.$invalid">
                Le format de cette adresse e-mail est invalide
              </template>
              <template v-else>
                {{ getErrorMessage("email") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          v-if="!client || client == null"
          :state="getState(v$,'password','model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="password"
            v-model.trim="model.password"
            type="password"
            :state="getState(v$,'password', 'model')"
            size="full"
            @blur="v$.model.password.$touch()"
            @input="v$.model.password.$reset()"
          >
            Mot de passe *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.password.required.$invalid">
                Merci de renseigner votre mot de passe
              </template>
              <template v-else>
                Votre mot de passe est incorrect
                <div class="u-color-grey-1">
                  Pour rappel votre mot de passe doit comporter au moins 6 caractères.
                </div>
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          v-if="!client || client == null"
          :state="getState(v$,'password_confirmation','model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="confirmPassword"
            v-model.trim="model.password_confirmation"
            type="password"
            :state="getState(v$,'password_confirmation', 'model')"
            size="full"
            @blur="v$.model.password_confirmation.$touch()"
            @input="v$.model.password_confirmation.$reset()"
          >
            Mot de passe Confirmation*
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.password_confirmation.required.$invalid">
                Merci de renseigner votre mot de passe
              </template>
              <template v-else>
                Votre mots de passe ne sont pas identiques
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'adresse', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="adresse"
            v-model.trim="model.adresse"
            :state="getState(v$,'adresse', 'model')"
            maxlength="255"
            size="full"
            @blur="v$.model.adresse.$touch()"
            @input="v$.model.adresse.$reset()"
          >
            Adresse *
          </FormInput>
          <template #message>
            <p>
              {{ getErrorMessage("adresse") }}
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'complement_adresse', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="complement"
            v-model.trim="model.complement_adresse"
            :state="getState(v$,'complement_adresse', 'model')"
            size="full"
          >
            Complément
          </FormInput>
          <template #message>
            <p>
              {{ getErrorMessage("complement_adresse") }}
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'cp', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="cp"
            v-model="model.cp"
            :state="getState(v$,'cp', 'model')"
            type="tel"
            size="full"
            @blur="v$.model.cp.$touch()"
            @input="v$.model.cp.$reset()"
          >
            Code Postal *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.cp.required.$invalid">
                Merci de renseigner votre code postal
              </template>
              <template v-else>
                {{ getErrorMessage("cp") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'ville', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="ville"
            v-model.trim="model.ville"
            :state="getState(v$,'ville', 'model')"
            size="full"
            @blur="v$.model.ville.$touch()"
            @input="v$.model.ville.$reset()"
          >
            Ville *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.ville.required.$invalid">
                Merci de renseigner votre ville
              </template>
              <template v-else>
                {{ getErrorMessage("ville") }}
              </template>
            </p>
          </template>
        </Formgroup>
        <label
          for="pays"
          class="u-block u-font-13 u-mb-xs u-color-text-light"
        >Pays </label>
        <FormGroup
          :state="getState(v$,'pays', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormSelect
            id="pays"
            v-model="model.pays"
            :state="getState(v$,'pays', 'model')"
            size="full"
            @blur="v$.model.pays.$touch()"
            @input="v$.model.pays.$reset()"
          >
            <FormSelectOption
              v-for="country in countries"
              :key="country"
              :value="country"
              :selected="country === 'France'"
            >
              {{ country }}
            </FormSelectOption>
          </FormSelect>

          <template #message>
            <p>
              <template v-if="v$.model.pays.required.$invalid">
                Merci de renseigner votre pays
              </template>
              {{ getErrorMessage("pays") }}
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'tel', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="tel"
            v-model.trim="model.tel"
            :state="getState(v$,'tel', 'model')"
            size="full"
            @blur="v$.model.tel.$touch()"
            @input="v$.model.tel.$reset()"
          >
            Téléphone *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.tel.required.$invalid">
                Merci de renseigner votre numéro de télephone
              </template>
              <template v-else-if="v$.model.tel.tel.$invalid">
                Ce numéro de téléphone est invalide
              </template>
              <template v-else>
                {{ getErrorMessage("tel") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <br>
        <p class="c-inscription-form__explanation">
          Pour mieux vous connaitre :
        </p>
        <br>
        <label class="u-block u-font-13 u-mb-xs u-color-text-light">
          Date de naissance
        </label>
        <FormGroup
          :state="getState(v$,'date_naissance','model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="date"
            v-model.trim="model.date_naissance"
            type="date"
            autocomplete
            :state="getState(v$,'date_naissance','model')"
            size="full"
            @blur="v$.model.date_naissance.$touch()"
            @input="v$.model.date_naissance.$reset()"
          />
          <template #message>
            <p>
              <template v-if="v$.model.date_naissance.birthdate.$invalid">
                Merci de saisir une date valide
              </template>
              <template v-else>
                {{ getErrorMessage("date_naissance") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'nombre_enfants', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="nbEnfants"
            v-model.trim="model.nombre_enfants"
            :state="getState(v$,'nombre_enfants', 'model')"
            type="number"
            size="full"
          >
            Nombre d’enfants
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.nombre_enfants.lessThanTen.$invalid">
                la valeur maximale possible est 10
              </template>
              <template v-else>
                {{ getErrorMessage("nombre_enfants","cette valeur est incorecte ") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <label class="u-block u-font-13 u-mb-xs u-color-text-light">Date de naissance du 1er enfant :</label>

        <FormGroup
          :state="getState(v$,'date_naissance_1er_enfant', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="date1"
            v-model.trim="model.date_naissance_1er_enfant"
            type="date"
            :state="getState(v$,'date_naissance_1er_enfant', 'model')"
            size="full"
            @blur="v$.model.date_naissance_1er_enfant.$touch()"
            @input="v$.model.date_naissance_1er_enfant.$reset()"
          />
          <template #message>
            <p>
              <template v-if="v$.model.date_naissance_1er_enfant.birthdate.$invalid">
                Merci de saisir une date valide
              </template>
              <template v-else>
                {{ getErrorMessage("date_naissance_1er_enfant") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <label class="u-block u-font-13 u-mb-xs u-color-text-light">Date de naissance du 2ème enfant :</label>
        <FormGroup
          :state="getState(v$,'date_naissance_2eme_enfant', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="date2"
            v-model.trim="model.date_naissance_2eme_enfant"
            type="date"
            :state="getState(v$,'date_naissance_2eme_enfant', 'model')"
            size="full"
            @blur="v$.model.date_naissance_2eme_enfant.$touch()"
            @input="v$.model.date_naissance_2eme_enfant.$reset()"
          />
          <template #message>
            <p>
              <template v-if="v$.model.date_naissance_2eme_enfant.birthdate.$invalid">
                Merci de saisir une date valide
              </template>
              <template v-else>
                {{ getErrorMessage("date_naissance_2eme_enfant") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <label class="u-block u-font-13 u-mb-xs u-color-text-light">Date de naissance du 3ème enfant :</label>

        <FormGroup
          :state="getState(v$,'date_naissance_3eme_enfant', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="date3"
            v-model.trim="model.date_naissance_3eme_enfant"
            type="date"
            :state="getState(v$,'date_naissance_3eme_enfant', 'model')"
            size="full"
            @blur="v$.model.date_naissance_3eme_enfant.$touch()"
            @input="v$.model.date_naissance_3eme_enfant.$reset()"
          />
          <template #message>
            <p>
              <template v-if="v$.model.date_naissance_3eme_enfant.birthdate.$invalid">
                Merci de saisir une date valide
              </template>
              <template v-else>
                {{ getErrorMessage("date_naissance_3eme_enfant") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <label class="u-block u-font-13 u-mb-xs u-color-text-light">Date de naissance du 4ème enfant :</label>

        <FormGroup
          :state="getState(v$,'date_naissance_4eme_enfant', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="date4"
            v-model.trim="model.date_naissance_4eme_enfant"
            type="date"
            :state="getState(v$,'date_naissance_4eme_enfant', 'model')"
            size="full"
            @blur="v$.model.date_naissance_4eme_enfant.$touch()"
            @input="v$.model.date_naissance_4eme_enfant.$reset()"
          />
          <template #message>
            <p>
              <template v-if="v$.model.date_naissance_4eme_enfant.birthdate.$invalid">
                Merci de saisir une date valide
              </template>
              <template v-else>
                {{ getErrorMessage("date_naissance_4eme_enfant") }}
              </template>
            </p>
          </template>
        </FormGroup>
      </FormFieldset>
      <FormGroup
        size="full"
        class="c-inscription-form__form-group"
      >
        <FormCheckbox v-model="model.optin_email_suivi_informations_promotionnelles_commerciales">
          J’accepte de recevoir par e-mail des informations promotionnelles et offres commerciales transmises
          par Cora.
        </FormCheckbox>
      </FormGroup>

      <FormGroup
        size="full"
        class="c-inscription-form__form-group"
        :state="getState(v$,'accepter_conditions_fidelite', 'model')"
      >
        <FormCheckbox
          v-model.trim="model.accepter_conditions_fidelite"
          :state="getState(v$,'accepter_conditions_fidelite', 'model')"
          @blur="v$.model.accepter_conditions_fidelite.$touch()"
          @input="v$.model.accepter_conditions_fidelite.$reset()"
        >
          <span>
            En cochant cette case, vous acceptez
            <LinkTo
              target="_blank"
              to="/legal/informations-juridiques/conditions-generales-fidelite"
            >
              les Conditions générales du programme de fidélité de Cora
            </LinkTo> *
          </span>
        </FormCheckbox>
        <template #message>
          <p>
            <template v-if="v$.model.accepter_conditions_fidelite.checked.$invalid">
              Vous devez accepter les les Conditions générales du programme de fidélité de Cora pour pouvoir
              soumettre votre demande
            </template>
          </p>
        </template>
      </FormGroup>
      <FormGroup
        size="full"
        class="c-inscription-form__form-group"
        :state="getState(v$,'accepter_conditions_compte_client', 'model')"
      >
        <FormCheckbox
          v-model.trim="model.accepter_conditions_compte_client"
          :state="getState(v$,'accepter_conditions_compte_client', 'model')"
          @blur="v$.model.accepter_conditions_compte_client.$touch()"
          @input="v$.model.accepter_conditions_compte_client.$reset()"
        >
          <span>
            En cochant cette case, vous acceptez
            <LinkTo
              target="_blank"
              to="/legal/informations-juridiques/cgu-compte-client-internet"
            >
              les Conditions générales d’utilisation du Compte Client Internet
            </LinkTo> *
          </span>
        </FormCheckbox>
        <template #message>
          <p>
            <template v-if="v$.model.accepter_conditions_compte_client.checked.$invalid">
              Vous devez accepter les Conditions générales d’utilisation du Compte Client Internet pour
              pouvoir soumettre votre demande
            </template>
          </p>
        </template>
      </FormGroup>
      <Captcha
        ref="captcha"
        @verify="verifyCaptcha"
      />
      <ButtonGroup center>
        <Button
          variant="secondary"
          radius="full"
          type="submit"
          value="Je valide ma demande"
          size="md"
          class="u-mt-sm u-flex-justify-center@sm"
          :disabled="state === states.LOADING"
        />
      </ButtonGroup>
    </Form>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/inscription-form.scss";
</style>
